import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  UserPlus, CheckCircle2, XCircle, Loader2, Mail, Phone, RefreshCw,
  Clock, Search, ShieldCheck, Link2, Copy, Share2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentRequests() {
  const { token, user } = useAuth();
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [reason, setReason] = useState('');
  const [tempPasswordShown, setTempPasswordShown] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Quick Invite Link — pre-fills the requester's company on /agent-request-access
  const inviteLink = useMemo(() => {
    if (!user?.company_id) return null;
    const base = window.location.origin;
    return `${base}/agent-request-access?company=${encodeURIComponent(user.company_id)}`;
  }, [user?.company_id]);

  const copyInvite = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success('Invite link copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy the link manually');
    }
  };

  const shareInvite = async () => {
    if (!inviteLink) return;
    const shareData = {
      title: 'Join our team on BodyCorp365',
      text: 'Use this link to request agent access to our management company on BodyCorp365.',
      url: inviteLink,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      copyInvite();
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        fetch(`${API_URL}/api/agent-requests/pending`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/agent-requests/all`, { headers }).then(r => r.json()),
      ]);
      setPending(p.requests || []);
      setAll(a.requests || []);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = (rows) => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.mobile || '').toLowerCase().includes(q)
    );
  };

  const submitAction = async () => {
    if (!selected || !action) return;
    setBusy(true);
    try {
      const body = action === 'reject' ? { reason } : {};
      const res = await fetch(`${API_URL}/api/agent-requests/${selected.request_id}/${action}`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        if (action === 'approve') {
          toast.success(`${selected.full_name} added — welcome email sent`);
          if (data.temp_password) setTempPasswordShown({ name: selected.full_name, password: data.temp_password });
        } else {
          toast.success('Request rejected');
        }
        setAction(null); setReason(''); setSelected(null);
        load();
      } else toast.error(data.detail || 'Action failed');
    } catch { toast.error('Network error'); }
    finally { setBusy(false); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
    };
    return <Badge className={map[s]}>{s}</Badge>;
  };

  const RequestRow = ({ r, showActions }) => (
    <div className="flex items-center justify-between p-4 bg-slate-700/40 rounded-lg hover:bg-slate-700/60 transition" data-testid={`request-row-${r.request_id}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-medium text-white">{r.full_name}</p>
          {statusBadge(r.status)}
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-slate-400">
          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>
          {r.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.mobile}</span>}
          <span className="text-slate-500">{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
        </div>
        {r.status === 'rejected' && r.rejection_reason && (
          <p className="text-xs text-slate-500 mt-1 italic">Reason: {r.rejection_reason}</p>
        )}
      </div>
      {showActions && r.status === 'pending' && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setSelected(r); setAction('approve'); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.request_id}`}>
            <UserPlus className="w-4 h-4 mr-1" /> Add Agent
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setSelected(r); setAction('reject'); }} className="border-red-600 text-red-400 hover:bg-red-500/10" data-testid={`reject-${r.request_id}`}>
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Pending Agent Requests</h1>
            <p className="text-slate-400 text-sm">Review and approve agents requesting to join your company</p>
          </div>
          <Button variant="outline" onClick={load} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, mobile…" className="pl-9 bg-slate-800 border-slate-700 text-slate-200" data-testid="search-input" />
        </div>

        {inviteLink && (
          <Card className="bg-indigo-900/30 border-indigo-700/50">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-5 h-5 text-indigo-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-white">Quick Invite Link</p>
                    <p className="text-xs text-indigo-200/70 mb-2">Share this link — your company is pre-selected, agents just fill in their details.</p>
                    <Input
                      value={inviteLink}
                      readOnly
                      className="bg-slate-900/50 border-indigo-700 text-indigo-100 font-mono text-xs"
                      onClick={(e) => e.target.select()}
                      data-testid="invite-link-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={copyInvite} className="bg-indigo-600 hover:bg-indigo-700" data-testid="copy-invite-link-btn">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <Button size="sm" variant="outline" onClick={shareInvite} className="border-indigo-600 text-indigo-200 hover:bg-indigo-500/10" data-testid="share-invite-link-btn">
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-400" /> Agent Onboarding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-700/50 border border-slate-700">
                <TabsTrigger value="pending" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white text-slate-300" data-testid="pending-tab">
                  <Clock className="w-4 h-4 mr-2" /> Pending ({pending.length})
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white text-slate-300" data-testid="history-tab">
                  History ({all.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="mt-4">
                {loading ? (
                  <div className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading…</div>
                ) : filtered(pending).length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No pending agent requests.</p>
                ) : (
                  <div className="space-y-2">
                    {filtered(pending).map(r => <RequestRow key={r.request_id} r={r} showActions />)}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="history" className="mt-4">
                {loading ? (
                  <div className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading…</div>
                ) : filtered(all).length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No requests yet.</p>
                ) : (
                  <div className="space-y-2">
                    {filtered(all).map(r => <RequestRow key={r.request_id} r={r} showActions={false} />)}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setReason(''); setSelected(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' ? 'Add Agent' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription className="text-slate-400" asChild>
              <div>
                {action === 'approve' ? (
                  <>This will create an account for <strong>{selected?.full_name}</strong>, link them to your company, generate a temporary password and email them onboarding instructions.</>
                ) : (
                  <>Tell <strong>{selected?.full_name}</strong> why their request was declined (optional, but recommended).</>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          {action === 'reject' && (
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="bg-slate-800 border-slate-700" data-testid="reject-reason-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAction(null); setReason(''); setSelected(null); }} className="border-slate-700">Cancel</Button>
            <Button onClick={submitAction} disabled={busy} className={action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} data-testid="confirm-action-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {action === 'approve' ? 'Add Agent' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Reveal (Platform Owner only) */}
      <Dialog open={!!tempPasswordShown} onOpenChange={(o) => { if (!o) setTempPasswordShown(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Agent Added</DialogTitle>
            <DialogDescription className="text-slate-400">
              {tempPasswordShown?.name} has been added. Welcome email sent. Temporary password (visible only this once):
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-slate-800 border border-slate-700 rounded-lg text-center font-mono text-lg tracking-wider text-emerald-300">
            {tempPasswordShown?.password}
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPasswordShown(null)} className="bg-indigo-600 hover:bg-indigo-700">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
